<template>
  <InfoModal
    :title="
      isPrivateFlow
        ? 'Information om överlåtelse av hyresrätt'
        : 'Information om överlåtelser'
    "
    class="information-modal"
    mobile-full-width
    :is-disabled="!hasReadInfo"
    :show-close-btn="hasReadInfo"
    @click="$emit('closeInfoModal')"
  >
    <div class="information-wrapper">
      <template v-if="isPrivateFlow">
        <div class="information-container">
          <h2>Vad krävs för att överlåta en hyreslägenhet?</h2>
          <p>
            För att få överlåta ett hyresavtal till någon annan krävs
            <b>samtycke från hyresvärden</b>. Enligt
            <b>12 kap. 34 § jordabalken</b> (hyreslagen) har en hyresgäst rätt
            att överlåta sin lägenhet till en <b>närstående</b> person,
            förutsatt att vissa krav är uppfyllda:
          </p>
          <ul>
            <li>
              Den som ska ta över hyresrätten måste ha
              <b>varaktigt bott tillsammans</b> med hyresgästen, normalt i minst
              <b>tre år</b>.
            </li>
            <li>
              Lägenheten ska ha använts som ett <b>gemensamt hushåll</b>, och
              varit <b>bådas enda stadigvarande bostad</b> under denna tid.
            </li>
            <li>
              Hyresgästen och den nya hyresgästen måste vara <b>närstående</b> –
              exempelvis make, sambo, barn eller förälder.
              <b
                >Vänner, inneboende eller andrahandshyresgäster räknas inte som
                närstående i</b
              >
              detta sammanhang.
            </li>
          </ul>
          <p>
            För att godkännas som ny hyresgäst måste personen också uppfylla
            hyresvärdens krav, till exempel:
          </p>
          <ul>
            <li>Ha tillräcklig <b>ekonomisk förmåga</b> att betala hyran.</li>
            <li>
              Inte ha orsakat <b>störningar eller andra problem</b> under
              tidigare boendetid.
            </li>
            <li>
              Den nuvarande hyresgästen ska kunna visa att hen har ett
              <b>nytt boende</b> ordnat.
            </li>
          </ul>
        </div>
        <div class="information-container">
          <h2>Överlåtelse vid dödsfall</h2>
          <p>
            Om hyresgästen avlider, kan hyresrätten överlåtas till en närstående
            person <b>under samma villkor som ovan</b>. I sådana fall är det
            <b>dödsboet</b>
            som ansöker om överlåtelsen. Den som företräder dödsboet måste ha en
            <b>fullmakt</b> undertecknad av samtliga dödsbodelägare.
          </p>
        </div>
        <div class="information-container">
          <h2>Övertagandets villkor</h2>
          <p>
            Om hyresvärden godkänner överlåtelsen, tar den nya hyresgästen över
            ett <b>befintligt hyresavtal</b>, vilket innebär att hen:
          </p>
          <ul>
            <li><b>Övertar lägenheten i befintligt skick</b></li>
            <li>
              <b>Ansvarar för eventuella skador</b>, installationer,
              förändringar eller onormalt slitage som uppstått tidigare
            </li>
            <li>
              <b>Blir betalningsansvarig för eventuella skulder</b> kopplade
              till boendet, fram till överlåtelsedatumet
            </li>
          </ul>
          <p><b>Observera:</b> Ingen besiktning genomförs före överlåtelsen.</p>
        </div>
        <div class="information-container">
          <h2>Handläggningstid</h2>
          <p>
            Hyresvärden hanterar ansökningarna i den ordning de kommer in.
            Handläggningstiden kan vara upp till <b>2 månader</b>.
          </p>
        </div>
        <div class="information-container">
          <h2>Om överlåtelsen nekas</h2>
          <p>
            Om hyresvärden inte godkänner en ansökan om överlåtelse har
            hyresgästen rätt att <b>ansöka om prövning hos Hyresnämnden</b>.
            Hyresnämnden gör då en självständig bedömning utifrån lagen och kan
            ge tillstånd till överlåtelsen även om hyresvärden har sagt nej,
            förutsatt att kriterierna är uppfyllda.
          </p>
        </div>
        <div class="information-container">
          <h2>Viktigt att veta</h2>
          <p>
            Om en överlåtelse sker <b>utan hyresvärdens samtycke</b> (eller utan
            beslut från Hyresnämnden), riskerar hyresgästen att
            <b>förlora sin hyresrätt</b>. Detsamma gäller om ansökan innehåller
            <b>felaktiga eller vilseledande uppgifter</b>.
          </p>
        </div>
      </template>
      <template v-else>
        <div class="information-container">
          <h2>Att tänka på vid överlåtelse av lokalhyresavtal</h2>
          <p>
            När en lokalhyresgäst vill överlåta sitt hyresavtal till någon annan
            – till exempel i samband med försäljning av en verksamhet – är det
            viktigt att känna till både de juridiska kraven och de praktiska
            stegen i processen.
          </p>
        </div>
        <div class="information-container">
          <h2>Hyresvärdens godkännande krävs</h2>
          <p>
            Enligt 12 kap. 32 § Jordabalken (hyreslagen) har en lokalhyresgäst
            rätt att överlåta sitt hyresavtal till någon som ska fortsätta
            bedriva verksamhet i lokalen,
            <b>förutsatt att hyresvärden ger sitt samtycke</b>. Om hyresvärden
            nekar detta utan giltiga skäl, kan hyresgästen i vissa fall ansöka
            om tillstånd hos <b>hyresnämnden</b> – men det krävs särskilda skäl
            för att få ett sådant godkännande.
          </p>
        </div>
        <div class="information-container">
          <h2>Skriftligt överlåtelseavtal – ett måste</h2>
          <p>
            Alla överlåtelser bör dokumenteras i ett <b>skriftligt avtal</b>. I
            överlåtelseavtalet bör det tydligt framgå:
          </p>
          <ul>
            <li>Om det är hela verksamheten som säljs, eller endast lokalen</li>
            <li>Om det är hela lokalen eller en del av den som omfattas</li>
          </ul>
        </div>
        <div class="information-container">
          <h2>Inför överlåtelsen – viktiga förberedelser</h2>
          <p>Innan en överlåtelse genomförs är det avgörande att:</p>
          <ul>
            <li>
              Den tillträdande hyresgästen har tagit del av det nuvarande
              hyresavtalet och accepterar alla villkor
            </li>
            <li>
              Alla parter är införstådda med att lokalen övertas
              <b>i befintligt skick</b>
            </li>
          </ul>
          <p>
            <b>Observera:</b> Hyresvärden har rätt att göra en bedömning av den
            nya hyresgästens ekonomiska och affärsmässiga förutsättningar innan
            samtycke lämnas.
          </p>
        </div>
        <div class="information-container">
          <h2>Vad innebär "i befintligt skick"?</h2>
          <p>
            När en lokal överlåts gäller normalt principen "befintligt skick",
            vilket innebär att:
          </p>
          <ul>
            <li><b>Ingen besiktning</b> sker innan överlåtelsen</li>
            <li>
              Den nya hyresgästen tar över <b>allt ansvar för lokalen</b>,
              inklusive:
              <ul>
                <li>Eventuella skador</li>
                <li>Installationer och ändringar som tidigare gjorts</li>
                <li>Överslitage eller brister</li>
                <li>Eventuella obetalda skulder som hänför sig till lokalen</li>
              </ul>
            </li>
          </ul>
          <p>
            Den nya hyresgästen träder alltså in i ett redan gällande
            avtalsförhållande och övertar därmed den tidigare hyresgästens
            rättigheter och skyldigheter.
          </p>
        </div>
        <div class="information-container">
          <h2>Tålamod och noggrann dokumentation</h2>
          <p>Överlåtelser tar ofta tid. Därför är det viktigt att:</p>
          <ul>
            <li>Vara ute i god tid</li>
            <li>Ha tålamod med processen</li>
            <li>Dokumentera allt skriftligt och noggrant</li>
            <li>
              Vara tydlig med vem som tar över, och att alla parter är överens
            </li>
          </ul>
        </div>
      </template>
    </div>
    <Checkbox
      v-if="!hasReadInfo"
      v-model="checkedReadInfo"
      name="checkedReadInfo"
      description=""
      :checked="checkedReadInfo"
      @change="checkedReadInfoToggle"
      >Jag har läst och tagit del av informationen</Checkbox
    >
    <BaseButton
      v-if="!hasReadInfo"
      :is-loading="false"
      :is-rounded="true"
      :is-fullwidth="isMobile"
      :is-small="isMobile"
      type="button"
      :is-disabled="!checkedReadInfo"
      @click="$emit('confirmAndCloseInfoModal')"
      ><BaseIconText :icon-url="svgs.ICONS.ARROW_RIGHT" :align-right="true">
        Godkänn och stäng
      </BaseIconText>
    </BaseButton>
    <BaseButton
      v-else
      :is-loading="false"
      :is-rounded="true"
      :is-fullwidth="isMobile"
      :is-small="isMobile"
      type="button"
      @click="$emit('closeInfoModal')"
      ><BaseIconText :icon-url="svgs.ICONS.ARROW_RIGHT" :align-right="true">
        Stäng
      </BaseIconText>
    </BaseButton>
  </InfoModal>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import InfoModal from '@/components/InfoModal.vue';
import Checkbox from '@/components/Checkbox.vue';
import BaseButton from './BaseButton.vue';
import BaseIconText from './BaseIconText.vue';
import svgs from '@/utils/icon-constants';

export default {
  name: 'SubletInformationModal',
  components: {
    InfoModal,
    Checkbox,
    BaseButton,
    BaseIconText
  },
  props: {
    hasReadInfo: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      checkedReadInfo: false,
      svgs
    };
  },
  computed: {
    ...mapState({
      application: state => state.subletApplication.application
    }),
    ...mapGetters({
      isMobile: 'app/isMobile',
      isPrivateFlow: 'ovApplication/isPrivateFlow'
    })
  },
  methods: {
    checkedReadInfoToggle() {
      this.checkedReadInfo = !this.checkedReadInfo;
    }
  }
};
</script>

<style lang="scss" scoped>
.information-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4rem;
  width: 100%;
  padding: 1rem 0;

  .information-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;

    h2,
    p {
      max-width: 100%;
      overflow-wrap: break-word;
    }

    ul {
      margin: 2rem 0 0 0;
      padding: 0 0 0 2rem;
    }
  }
}
</style>
